import React, { useEffect, useRef, useState } from "react";

function OneGeoMapWithMarker({ markerLocation, modalKey, polygon }) {
	const containerId = useRef(`osmb-container-${Date.now()}`);
	const osmbInstanceRef = useRef(null);
	const overlayRef = useRef(null);
	const [scriptLoaded, setScriptLoaded] = useState(false);

	// 1) Dynamically load OSMBuildings script
	const loadOSMBuildingsScript = () => {
		if (document.getElementById("osmb-script")) {
			setScriptLoaded(true);
			return;
		}

		const script = document.createElement("script");
		script.id = "osmb-script";
		script.src = "https://cdn.osmbuildings.org/4.1.1/OSMBuildings.js";
		script.onload = () => setScriptLoaded(true);
		script.onerror = () => console.error("Failed to load OSMBuildings.js");
		document.body.appendChild(script);
	};

	useEffect(() => {
		loadOSMBuildingsScript();
	}, []);

	// 2) Initialize the map and overlay marker
	useEffect(() => {
		if (!scriptLoaded || !window.OSMBuildings) return;

		// Cleanup existing instance
		osmbInstanceRef.current?.destroy();
		const osmb = new window.OSMBuildings({
			container: containerId.current,
			zoom: 16,
			tilt: 30,
			position: {
				latitude: markerLocation?.lat || 52.52,
				longitude: markerLocation?.lng || 13.38
			},
			rotation: 300,
			effects: ["shadows"]
		});
		osmbInstanceRef.current = osmb;

		osmb.addMapTiles("https://tile-a.openstreetmap.fr/hot/{z}/{x}/{y}.png");
		osmb.addGeoJSONTiles("https://{s}.data.osmbuildings.org/0.2/59fcc2e8/tile/{z}/{x}/{y}.json");

		// Position HTML overlay instead of built-in marker
		const updateOverlay = () => {
			if (!markerLocation || !overlayRef.current) return;
			const { x, y } = osmb.project(markerLocation.lat, markerLocation.lng, 20);
			overlayRef.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -100%)`;
		};

		osmb.on("change", updateOverlay);
		updateOverlay();

		return () => {
			osmb.off("change", updateOverlay);
			osmb.destroy();
			osmbInstanceRef.current = null;
		};
	}, [scriptLoaded, markerLocation, modalKey]);

	// 3) Polygon overlay as before
	useEffect(() => {
		const osmb = osmbInstanceRef.current;
		if (!osmb || !polygon?.coordinates?.length) return;
		const coords = polygon.coordinates.map(c => [c.lng, c.lat]);
		osmb.addGeoJSON({
			type: "FeatureCollection",
			features: [
				{
					type: "Feature",
					geometry: { type: "Polygon", coordinates: [coords] },
					properties: { color: "#F10606", roofColor: "#F10606", height: 50 }
				}
			]
		});
	}, [polygon]);

	// 4) Render map and pointy overlay marker
	return (
		<div style={{ position: "relative", width: "100%", height: "400px" }}>
			{/* OSMBuildings canvas */}
			<div id={containerId.current} style={{ width: "100%", height: "100%" }} />

			{/* Pointy overlay marker */}
			<div
				ref={overlayRef}
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "24px",
					height: "36px",
					pointerEvents: "none",
					transform: "translate(-50%, -100%)"
				}}
			>
				{/* Circle head */}
				<div
					style={{
						width: "24px",
						height: "24px",
						background: "rgba(255,0,0,0.7)",
						border: "3px solid white",
						borderRadius: "50%",
						boxSizing: "border-box"
					}}
				/>
				{/* Triangle pointer */}
				<div
					style={{
						width: 0,
						height: 0,
						borderLeft: "12px solid transparent",
						borderRight: "12px solid transparent",
						borderTop: "12px solid rgba(255,0,0,0.7)",
						margin: "0 auto"
					}}
				/>
			</div>
		</div>
	);
}

export default OneGeoMapWithMarker;
